/**
 * CorrelationScatterPlot component - Scatter plot visualization for metric correlations.
 *
 * Displays correlation between two metrics with:
 * - Scatter plot points
 * - Trend line
 * - Correlation strength indicator
 * - Material Design styling
 */

import React from 'react';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { CorrelationInsight } from '../../types/progress';

interface CorrelationScatterPlotProps {
  correlation: CorrelationInsight;
  className?: string;
}

const CorrelationScatterPlot: React.FC<CorrelationScatterPlotProps> = ({
  correlation,
  className = ''
}) => {
  // Format metric names for display
  const formatMetricName = (metric: string) => {
    return metric.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  // Get strength color
  const getStrengthColor = (strength: string) => {
    switch (strength) {
      case 'strong':
        return '#16a34a'; // green-600
      case 'moderate':
        return '#2563eb'; // blue-600
      case 'weak':
        return '#f59e0b'; // amber-500
      default:
        return '#6b7280'; // neutral-500
    }
  };

  // Get confidence badge color
  const getConfidenceBadge = (confidence: string) => {
    switch (confidence) {
      case 'high':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'medium':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'low':
        return 'bg-amber-100 text-amber-800 border-amber-300';
      default:
        return 'bg-neutral-100 text-neutral-800 border-neutral-300';
    }
  };

  // Format scatter data for Recharts
  const scatterData = correlation.scatter_data.map((point) => ({
    x: point.x,
    y: point.y,
    date: point.date
  }));

  return (
    <div className={`card shadow-soft ${className}`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h4 className="text-lg font-semibold text-neutral-900 mb-1">
            {formatMetricName(correlation.metric_x)} vs {formatMetricName(correlation.metric_y)}
          </h4>
          <div className="flex items-center space-x-3">
            <span
              className="px-3 py-1 rounded-lg border font-semibold text-sm"
              style={{
                backgroundColor: `${getStrengthColor(correlation.strength)}20`,
                borderColor: getStrengthColor(correlation.strength),
                color: getStrengthColor(correlation.strength)
              }}
            >
              {correlation.strength.toUpperCase()} {correlation.direction === 'positive' ? '↗' : '↘'}
            </span>
            <span className={`px-3 py-1 rounded-lg border text-xs font-semibold ${getConfidenceBadge(correlation.confidence)}`}>
              {correlation.confidence.toUpperCase()} CONFIDENCE
            </span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-sm text-neutral-600 mb-1">Correlation</div>
          <div className="text-2xl font-bold" style={{ color: getStrengthColor(correlation.strength) }}>
            {correlation.correlation_coefficient.toFixed(3)}
          </div>
          <div className="text-xs text-neutral-500">
            p = {correlation.p_value.toFixed(4)}
          </div>
        </div>
      </div>

      {/* Scatter Plot */}
      <div className="bg-gradient-to-br from-neutral-50 to-white p-4 rounded-xl border border-neutral-200 mb-4">
        <ResponsiveContainer width="100%" height={300}>
          <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis
              type="number"
              dataKey="x"
              name={formatMetricName(correlation.metric_x)}
              tick={{ fill: '#6b7280', fontSize: 12 }}
              tickLine={{ stroke: '#6b7280' }}
              label={{
                value: formatMetricName(correlation.metric_x),
                position: 'insideBottom',
                offset: -10,
                fill: '#6b7280',
                fontSize: 12
              }}
            />
            <YAxis
              type="number"
              dataKey="y"
              name={formatMetricName(correlation.metric_y)}
              tick={{ fill: '#6b7280', fontSize: 12 }}
              tickLine={{ stroke: '#6b7280' }}
              label={{
                value: formatMetricName(correlation.metric_y),
                angle: -90,
                position: 'insideLeft',
                fill: '#6b7280',
                fontSize: 12
              }}
            />
            <Tooltip
              cursor={{ strokeDasharray: '3 3' }}
              contentStyle={{
                backgroundColor: '#fff',
                border: '2px solid #C98CCC',
                borderRadius: '0.75rem',
                padding: '8px 12px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
              }}
              formatter={(value: any) => [value.toFixed(1), '']}
              labelFormatter={(label) => `Date: ${label}`}
            />
            <Scatter
              data={scatterData}
              fill={getStrengthColor(correlation.strength)}
              fillOpacity={0.6}
            />
          </ScatterChart>
        </ResponsiveContainer>
      </div>

      {/* Interpretation */}
      <div className="bg-gradient-to-br from-primary-50 to-white border border-primary-200 rounded-xl p-4">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 mt-0.5">
            <span className="text-2xl">💡</span>
          </div>
          <div>
            <p className="text-neutral-800 leading-relaxed">{correlation.interpretation}</p>
            {correlation.is_significant && (
              <p className="text-sm text-neutral-600 mt-2">
                Based on {correlation.data_points_count} data points.
                {correlation.p_value < 0.01
                  ? ' Highly statistically significant.'
                  : ' Statistically significant.'}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CorrelationScatterPlot;
